<img
  id="logo"
  ngSrc="assets/img/ogi-logo.png"
  width="200"
  height="51"
  alt="OGI Logo"
  priority
/>
<div id="page">
  <div id="container">
    @if (view() === "enrollment") {
      <div id="enrollment">
        <h4>Setup Two-Factor Authentication</h4>
        @if (isInitialLoad()) {
          <div id="loading-spinner">
            <app-loading-spinner></app-loading-spinner>
          </div>
        }
        <div [style.visibility]="isInitialLoad() ? 'hidden' : 'visible'">
          <div class="step">
            <div class="circle">1</div>
            <div class="body1">
              Scan the QR code using any authentication application on your
              phone (e.g. Google Authenticator, Microsoft Authenticator) or
              enter the following code:
            </div>
          </div>
          @if (qrCodeUrl()) {
            <div class="qr-container">
              <qrcode
                [qrdata]="qrCodeUrl()!"
                [width]="130"
                [errorCorrectionLevel]="'M'"
                ariaLabel="Two-factor authentication QR code"
              ></qrcode>
            </div>
            <div class="copy-qr-code">
              <div class="subtitle2">{{ sharedKey() }}</div>
              <app-mat-icon-button
                iconName="content_copy"
                ariaLabel="copy"
                (buttonClicked)="copyCode()"
              ></app-mat-icon-button>
            </div>
          } @else if (error()?.message) {
            <div class="retry--container">
              <app-mat-outlined-button
                label="Try again"
                ariaLabel="Try again"
                buttonId="retry-qr-btn"
                iconName="refresh"
                iconPosition="right"
                (buttonClicked)="getQrCode()"
              >
              </app-mat-outlined-button>
            </div>
          }
          @if (clipboardError()) {
            <p class="error body2" role="alert">{{ clipboardError() }}</p>
          }
          <div class="step">
            <div class="circle">2</div>
            <div class="body1">
              Enter the 6 digit confirmation code shown on the app:
            </div>
          </div>
          <form [formGroup]="confirmationCodeForm">
            <div class="digit-inputs" (paste)="onPaste($event)">
              <input
                #digitInput
                type="text"
                maxlength="1"
                formControlName="digit0"
                aria-label="Digit 1 of 6"
                inputmode="numeric"
                autocomplete="off"
                autofocus
                (focus)="onDigitFocus($event)"
                (input)="onDigitInput(0, $event)"
                (keydown)="onDigitKeydown($event, 0)"
              />
              <input
                #digitInput
                type="text"
                maxlength="1"
                formControlName="digit1"
                aria-label="Digit 2 of 6"
                inputmode="numeric"
                autocomplete="off"
                (focus)="onDigitFocus($event)"
                (input)="onDigitInput(1, $event)"
                (keydown)="onDigitKeydown($event, 1)"
              />
              <input
                #digitInput
                type="text"
                maxlength="1"
                formControlName="digit2"
                aria-label="Digit 3 of 6"
                inputmode="numeric"
                autocomplete="off"
                (focus)="onDigitFocus($event)"
                (input)="onDigitInput(2, $event)"
                (keydown)="onDigitKeydown($event, 2)"
              />
              <input
                #digitInput
                type="text"
                maxlength="1"
                formControlName="digit3"
                aria-label="Digit 4 of 6"
                inputmode="numeric"
                autocomplete="off"
                (focus)="onDigitFocus($event)"
                (input)="onDigitInput(3, $event)"
                (keydown)="onDigitKeydown($event, 3)"
              />
              <input
                #digitInput
                type="text"
                maxlength="1"
                formControlName="digit4"
                aria-label="Digit 5 of 6"
                inputmode="numeric"
                autocomplete="off"
                (focus)="onDigitFocus($event)"
                (input)="onDigitInput(4, $event)"
                (keydown)="onDigitKeydown($event, 4)"
              />
              <input
                #digitInput
                type="text"
                maxlength="1"
                formControlName="digit5"
                aria-label="Digit 6 of 6"
                inputmode="numeric"
                autocomplete="off"
                (focus)="onDigitFocus($event)"
                (input)="onDigitInput(5, $event)"
                (keydown)="onDigitKeydown($event, 5)"
              />
            </div>
            @if (error()?.message) {
              <p class="error">{{ error().message }}</p>
            }
          </form>
          <app-button
            buttonStyle="standard"
            label="Continue"
            buttonId="continue-btn"
            [isDisabled]="confirmationCodeForm.invalid"
            [isProcessing]="isProcessing()"
            (buttonClicked)="handleSubmit()"
          >
          </app-button>
        </div>
      </div>
    } @else if (view() === "already-enrolled") {
      <div id="no-codes">
        <h4 #viewHeading tabindex="-1">Two-Factor Authentication Enabled</h4>
        <p class="no-codes" role="alert">{{ error()?.message }}</p>
        <app-button
          buttonStyle="standard"
          label="Continue"
          buttonId="already-enrolled-continue-btn"
          (buttonClicked)="onRecoveryCodesDone()"
        >
        </app-button>
      </div>
    } @else if (view() === "recovery-codes") {
      <div id="recovery-codes">
        <h4 #viewHeading tabindex="-1">Save your recovery codes</h4>
        <div class="body1">
          If you lose access to your authentication app, you can sign in using
          your recovery codes. Each code can only be used once. Save these codes
          somewhere safe - you won't be able to view them again.
        </div>
        <div class="codes">
          @for (code of recoveryCodes() ?? []; track code) {
            <div class="code">{{ code }}</div>
          }
        </div>
        <div class="actions">
          <div class="info">
            <mat-icon>info</mat-icon>
            <div class="body1">Keep your recovery codes in a secure place</div>
          </div>
          <app-mat-outlined-button
            label="Copy"
            ariaLabel="Copy Recovery Codes"
            buttonId="copy-recovery-codes-btn"
            iconName="arrow_forward"
            iconPosition="right"
            (buttonClicked)="copyRecoveryCodes()"
          >
          </app-mat-outlined-button>
        </div>
        @if (clipboardError()) {
          <div class="error" role="alert">{{ clipboardError() }}</div>
        }
        <app-checkbox
          [checkbox]="savedRecoveryCodes"
          [parentForm]="recoveryCodeForm"
        ></app-checkbox>
        <app-button
          buttonStyle="standard"
          label="Done"
          buttonId="done-btn"
          [isDisabled]="recoveryCodeForm.invalid"
          (buttonClicked)="onRecoveryCodesDone()"
        >
        </app-button>
      </div>
    } @else if (view() === "org-pending-2fa") {
      <div id="org-pending-2fa">
        <mat-icon>check_circle</mat-icon>
        <h4 #viewHeading tabindex="-1">2FA Setup Complete</h4>
        <p class="body1">
          Return to your organisation settings to finish enabling 2FA for your
          organisation.
        </p>
        <app-button
          buttonStyle="standard"
          label="Go to Organisation"
          buttonId="go-to-org-btn"
          (buttonClicked)="goToOrganisation()"
        >
        </app-button>
      </div>
    }
  </div>
</div>
